import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, LogOut, User as UserIcon, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { initials } from '@/lib/format';
import { signOut, ROLE_LABELS } from '@/lib/auth';
import { useUser } from '@/hooks/useUser';
import { t } from '@/locales/i18n';

interface TopbarProps {
  onOpenCommand: () => void;
}

export function Topbar({ onOpenCommand }: TopbarProps): JSX.Element {
  const { metadata } = useUser();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Sprint 10.2 — Sécurité : on n'expose plus l'email en fallback dans la topbar.
  // Si l'user n'a pas de full_name dans metadata, fallback générique "Utilisateur".
  const displayName = metadata?.full_name ?? 'Utilisateur';
  const roleLabel = metadata ? ROLE_LABELS[metadata.role] : null;

  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-background px-4">
      <button
        type="button"
        onClick={onOpenCommand}
        className="flex h-9 w-72 max-w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">{t('nav.search')}</span>
        <kbd className="ml-auto hidden rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={t('topbar.themeToggle')}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-secondary"
              aria-label={t('topbar.userMenu')}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {initials(displayName)}
              </div>
              <div className="hidden text-left sm:block">
                <div className="text-sm font-medium leading-tight">{displayName}</div>
                {roleLabel && (
                  <div className="text-[11px] leading-tight text-muted-foreground">{roleLabel}</div>
                )}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{displayName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <UserIcon className="h-4 w-4" />
              {t('topbar.profile')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={async () => {
                await signOut();
                navigate('/login');
              }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
